'use client'

import { useTranslations } from 'next-intl'
import type { Formats, TranslationValues } from 'next-intl'
import { useUserQuery } from 'auth/hooks'
import { isLoggedInQuerySelector } from 'auth/selectors'

import { CheckIcon } from '@web/components/icons/check'
import { getCategoryDetails } from '@web/components/content-api/config'

interface NoAccessDetailsProps {
  className?: string
  rootCategoryId: string
  totalProducts: number
}

export function DetailsWithoutAccess({ rootCategoryId, totalProducts }: NoAccessDetailsProps) {
  const t = useTranslations('prod.product_description_page.buy')
  const details = getCategoryDetails(
    rootCategoryId,
    t as <TargetKey extends string>(
      key: TargetKey,
      values?: TranslationValues | undefined,
      formats?: Partial<Formats> | undefined,
    ) => string,
  )

  const { isPending, data: userData } = useUserQuery(undefined, {
    select: data => data?.me?.user,
  })
  const isLoggedIn =
    !isPending &&
    isLoggedInQuerySelector({
      me: { user: userData },
    })
  const isAllAccess = !isPending && !!userData?.subscriptionAccess.allAccess.isSubscribed
  const isEligibleForAllAccessFreeTrial =
    !isPending && !!userData?.subscriptionAccess.allAccess.isEligibleForFreeTrial
  const showFree = !isLoggedIn || isAllAccess || isEligibleForAllAccessFreeTrial

  return (
    <div className="relative p-4">
      {showFree ? (
        <div className="text-cf-white rounded-tr-4 rounded-bl-4 animate-fade-in absolute -right-[1px] -top-[1px] bg-[#E21743] px-4 py-2 font-bold uppercase">
          {t('free')}
        </div>
      ) : null}
      <div className="text-16 mb-2 pr-[110px] font-bold">
        {t('title', {
          productType: details.label,
        })}
      </div>
      <ul className="text-12 mb-4">
        <li className="mb-1 flex items-center justify-start gap-2">
          <CheckIcon className="shrink-0" />
          {t('info_1', {
            productsNumber: totalProducts,
            categoryName: details.labelPlural,
          })}
        </li>
        <li className="mb-1 flex items-center justify-start gap-2">
          <CheckIcon className="shrink-0" />
          {t('info_2')}
        </li>
        <li className="mb-1 flex items-center justify-start gap-2">
          <CheckIcon className="shrink-0" />
          {t('info_3', {
            categoryName: details.labelPlural,
          })}
        </li>
        <li className="mb-1 flex items-center justify-start gap-2">
          <CheckIcon className="shrink-0" />
          {t('info_4')}
        </li>
        <li className="mb-1 flex items-center justify-start gap-2">
          <CheckIcon className="shrink-0" />
          {t('info_5')}
        </li>
      </ul>
    </div>
  )
}
